import { useState, useEffect } from 'react';

const svgCache = new Map<string, string>();
const svgInflight = new Map<string, Promise<string>>();

const loadSvg = (name: string): Promise<string> => {
  const cached = svgCache.get(name);
  if (cached !== undefined) {
    return Promise.resolve(cached);
  }

  const inflight = svgInflight.get(name);
  if (inflight) {
    return inflight;
  }

  const promise = (async () => {
    const svgModule = await import(`@/assets/icons/${name}.svg`);
    const response = await fetch(svgModule.default);
    if (!response.ok) {
      throw new Error(`SVG not found: ${name}`);
    }

    const svgText = await response.text();
    svgCache.set(name, svgText);
    return svgText;
  })().finally(() => {
    svgInflight.delete(name);
  });

  svgInflight.set(name, promise);
  return promise;
};

export const useSvgIcon = (name: string) => {
  const [svgContent, setSvgContent] = useState<string | null>(() => svgCache.get(name) ?? null);
  const [isLoaded, setIsLoaded] = useState(() => svgCache.has(name));
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (svgCache.has(name)) {
      setSvgContent(svgCache.get(name)!);
      setIsLoaded(true);
      setError(null);
      return;
    }

    setSvgContent(null);
    setIsLoaded(false);
    setError(null);

    loadSvg(name)
      .then((svgText) => {
        if (cancelled) return;
        setSvgContent(svgText);
        setIsLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err as Error);
        setIsLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [name]);

  return { svgContent, isLoaded, error };
};

export const usePreloadIcons = (iconNames: string[]) => {
  useEffect(() => {
    iconNames.forEach((iconName) => {
      loadSvg(iconName).catch((err) => {
        console.warn(`Failed to preload icon: ${iconName}`, err);
      });
    });
  }, [iconNames]);
};
